import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { ProminentActionBanner } from '@/components/notifications/ProminentActionBanner/ProminentActionBanner';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';

import { useMarkValidationQuestionNotificationsRead } from '../hooks/useMarkValidationQuestionNotificationsRead';
import { useOpenValidationQuestions } from '../hooks/useOpenValidationQuestions';
import { SetupValidationPrompt } from './SetupValidationPrompt';

type ValidationQuestionsBannerProps = {
  householdId: string;
  conversationId: string | null;
} & (
  | { variant: 'setup'; householdName: string; initialSetup?: boolean }
  | {
      variant?: 'banner';
      householdName?: never;
      initialSetup?: never;
    }
);

/**
 * Show pending validation on the legacy overview or setup
 * screen. Read-only users can see household status but cannot answer questions.
 *
 * Opens the enhanced form when enabled, or the existing question conversation
 * in household chat when the flag is off.
 */
export function ValidationQuestionsBanner({
  householdId,
  conversationId,
  variant,
  householdName,
  initialSetup,
}: ValidationQuestionsBannerProps) {
  const canEditHousehold = useCanEditHouseholdData();
  const markValidationQuestionNotificationsRead =
    useMarkValidationQuestionNotificationsRead(householdId);
  const openValidationQuestions = useOpenValidationQuestions();
  const trackUserEvent = useTrackUserEvent();

  if (!conversationId || (!canEditHousehold && variant !== 'setup')) {
    return null;
  }

  const handleClick = () => {
    if (!canEditHousehold || !conversationId) return;
    trackUserEvent('household_chat validation questions banner clicked', {
      householdId,
    });
    markValidationQuestionNotificationsRead();
    openValidationQuestions(householdId, conversationId);
  };

  if (variant === 'setup') {
    return (
      <SetupValidationPrompt
        householdName={householdName}
        onAnswer={handleClick}
        initialSetup={initialSetup}
        disabled={!canEditHousehold}
      />
    );
  }

  return (
    <ProminentActionBanner
      icon={Stars01Icon}
      header="Validation questions required"
      description="Answer a few questions in order to build out the household and create deliverables"
      action={{ label: 'Answer questions', onClick: handleClick }}
    />
  );
}
